import { exposeWhenVisible, variantOf } from '@theme/hds-experiments';

/**
 * A/B-test retourkosten verf (SHO-10638).
 *
 * Control = «Gratis 30 dagen retourneren» zoals het nu is. Treatment = €4,99 retourkosten per
 * blik, met erbij dat retour van op kleur gemengde verf uniek is. Het verschil staat op twee
 * plekken op de productpagina: de retour-USP onder de koopknop (`blocks/popup-link.liquid`) en
 * de tekst in «Bezorgen & retourneren» (`blocks/bezorg-retour.liquid`). Welke producten meedoen
 * en de teksten staan in `snippets/retour-experiment.liquid` en de thema-instellingen.
 *
 * Liquid rendert op elke plek beide teksten, gemarkeerd met `data-arm`. Dit script zet de arm
 * van de bezoeker als `data-retour-arm` en wat er getoond wordt als `data-retour-toon`; de CSS in
 * base.css toont alleen die tekst. Tot dat gebeurd is, is de plek onzichtbaar, zodat de
 * treatment-arm nooit even «gratis» ziet.
 *
 * Proefblikjes blijven gratis te retourneren. Zijn ze een variant van het verfproduct (100 ml,
 * 60 ml; `data-retour-gratis`), dan toont ook treatment de gratis tekst zolang zo'n variant
 * gekozen is. De bezoeker telt pas mee als hij een variant mét retourkosten voor zich heeft: tot
 * dan zien beide armen hetzelfde, en wie alleen een proefblikje bekijkt hoort niet in de noemer.
 *
 * De blootstelling telt op de USP, zodra die in beeld komt, in beide armen op hetzelfde punt.
 * Staat er geen USP (ander template), dan telt de tekst in het accordeon. De arm reist via
 * `expose()` als cart-attribuut `_exp_<id>` mee naar de order.
 *
 * Horizon morpht <main> bij een variant-wissel (combined listings); de nieuwe knopen komen
 * zonder arm binnen, dus na `hds:main-morphed` opnieuw toepassen. Een wissel zonder morph komt
 * binnen als `variant:update` (ThemeEvents.variantUpdate).
 */

const SELECTOR = '[data-retour-exp]';

/** @param {HTMLElement} place */
function isGratis(place) {
  const variant = place.dataset.variantId ?? '';
  if (!variant) return false;
  return (place.dataset.retourGratis ?? '').split(',').includes(variant);
}

/**
 * Zet op elke retour-plek onder `root` de arm van deze bezoeker en wat er te zien is, en meld
 * de blootstelling aan zodra er een variant met retourkosten gekozen is. Idempotent: de arm
 * ligt per bezoeker vast en `exposeWhenVisible` telt één keer per pagina.
 *
 * @param {ParentNode} [root]
 */
export function applyRetourExperiment(root = document) {
  /** @type {Map<string, HTMLElement[]>} */
  const perExperiment = new Map();

  for (const place of root.querySelectorAll(SELECTOR)) {
    if (!(place instanceof HTMLElement)) continue;
    const id = place.dataset.retourExp;
    if (!id) continue;

    const arm = variantOf(id);
    place.dataset.retourArm = arm;
    place.dataset.retourToon = isGratis(place) ? 'control' : arm;

    const list = perExperiment.get(id) ?? [];
    list.push(place);
    perExperiment.set(id, list);
  }

  for (const [id, places] of perExperiment) {
    const anchor = places.find((place) => place.dataset.retourPlek === 'usp') ?? places[0];
    if (!anchor || isGratis(anchor)) continue;
    exposeWhenVisible(id, anchor, {
      placement: anchor.dataset.retourPlek ?? '',
      product_id: anchor.dataset.productId ?? '',
    });
  }
}

/** @param {string} variantId */
function selectVariant(variantId) {
  for (const place of document.querySelectorAll(SELECTOR)) {
    if (place instanceof HTMLElement) place.dataset.variantId = variantId;
  }
  applyRetourExperiment();
}

try {
  // ?variant= in de URL gaat voor, net als in bezorg-retour.js.
  const fromUrl = new URLSearchParams(window.location.search).get('variant');
  if (fromUrl) selectVariant(fromUrl);
  else applyRetourExperiment();

  document.addEventListener('hds:main-morphed', () => applyRetourExperiment());
  document.addEventListener('variant:update', (event) => {
    const id = /** @type {any} */ (event).detail?.resource?.id;
    if (id) selectVariant(String(id));
  });
} catch {
  // Meten mag winkelen nooit breken; zonder arm toont de CSS na 2 s gewoon de control.
}
